import {
  useState,
  memo,
  useEffect,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  Divider,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { TABLE_ROWS_PER_PAGE } from 'src/utils/constants';
import Loader from 'src/common/Loader';
import useDidUpdate from 'src/hooks/useDidUpdate';
import { startCase } from 'lodash';
import { getAlignByColumnId } from 'src/utils/tableHeaders';

const OtherReportsTable = ({ reportName, onLimitChange, ...rest }) => {
  const {
    loading, otherReportList, totalCount, nextPage
  } = useSelector((state) => state.reports);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [tableHeaders, setTableHeaders] = useState([]);
  const otherReportsAlignById = useMemo(() => getAlignByColumnId(tableHeaders), [tableHeaders]);

  useEffect(() => {
    if (otherReportList?.length > 0) {
      const headerKeys = Object.keys(otherReportList[0]);
      const formattedTableHeaders = headerKeys.map((key) => ({
        id: key,
        name: startCase(key),
        align: 'center'
      }));
      setTableHeaders(formattedTableHeaders);
      setOrderBy(headerKeys[0]);
    }
  }, [otherReportList]);

  useDidUpdate(() => {
    setPage(0);
  }, [reportName]);

  const handleLimitChange = (event) => {
    const newLimit = event.target.value;
    setPage(0);
    setLimit(newLimit);
    if (nextPage) {
      onLimitChange({ pageNo: 1, limit: newLimit });
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    if (nextPage) {
      onLimitChange({ pageNo: newPage + 1, limit });
    }
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Card {...rest}>
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="h5">{`Report Table - ${startCase(reportName)}`}</Typography>
      </Box>
      <Divider />
      {otherReportList?.length > 0 && (
        <PerfectScrollbar>
          <Box sx={{ minWidth: { xs: 760, sm: 900, md: 1050 }, minHeight: 200 }}>
            {loading ? <Loader />
              : (
                <Table>
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    headCells={tableHeaders}
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                    {otherReportList
                      .slice()
                      .sort(getComparator(order, orderBy))
                      .map((report, i) => (
                        <TableRow key={`other-reports-${i.toString()}`}>
                          {tableHeaders.map((header) => (
                            <TableCell align={otherReportsAlignById[header.id]} key={header.id}>
                              {report[header.id]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
          </Box>
        </PerfectScrollbar>
      )}
      <TablePagination
        component="div"
        count={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={page}
        rowsPerPage={limit}
        rowsPerPageOptions={TABLE_ROWS_PER_PAGE}
      />
    </Card>
  );
};

OtherReportsTable.propTypes = {
  reportName: PropTypes.string,
  onLimitChange: PropTypes.func
};

export default memo(OtherReportsTable);
